import React from "react";
import { Checkbox } from "semantic-ui-react";
import classNames from "classnames";
import { TaskEventModal } from "../../TaskEventModal";
import {
  useActiveTaskSelector,
  useCompletedChecklistMapSelector
} from "../../../../../../../store/selectors/task/task.selectors";
import {CheckList, ChecklistItemModel} from "../../../../../../../models/checklist-model";
import EventTextModel from "../../../../../../../models/event-text-model.enum";
import TaskStatusModel from "../../../../../../../models/responses/task-status.model";
import ChecklistUtils from "../../../../../../../utils/checklist/checklist.utils";
import {
  useActiveProjectCalendarsSelector,
  useActiveProjectSelector,
  useCpmMapSelector
} from "../../../../../../../store/selectors/project.selectors";
import {useUserSelector} from "../../../../../../../store/selectors/authorization.selectors";
import {
  convertDateToIndex,
  convertIndexToSeconds
} from "../../../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import styles from "../../../../ChecklistPage/Checklist.module.scss";
import moment from "moment";
import ChecklistAttachmentInput from "../../../../ChecklistPage/components/ChecklistAttachmentInput";
import {useWorkPackageMapSelector} from "../../../../../../../store/selectors/work-packages.selectors";

interface InjectedProps {
  taskChecklist: any[];
  onChangeHandler: (item: any) => void;
  removeHandler: (itemId: string) => void;
  eventText: EventTextModel;
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
  activeItem: CheckList | null;
  setActiveItem: React.Dispatch<React.SetStateAction<CheckList | null>>;
  disabled: boolean;
  type: 'task' | 'workPackage';
}

const TaskChecklist: React.FC<InjectedProps> = ({
  taskChecklist,
  onChangeHandler,
  removeHandler,
  eventText,
  opened,
  setOpened,
  activeItem,
  setActiveItem,
  disabled,
  type
}) => {
  const activeTask = useActiveTaskSelector();
  const activeProject = useActiveProjectSelector();
  const completeChecklist = useCompletedChecklistMapSelector();
  const user = useUserSelector();
  const cpmMap = useCpmMapSelector();
  const calendarsMap = useActiveProjectCalendarsSelector();
  const cpmTask = cpmMap.get(activeTask!.task_id);
  const workingHours = calendarsMap.get(`whd:${cpmTask?.cal_id}`);
  const dataDate = new Date().setHours(0, 0, 0, 0) / 1000;
  const dataDateIndex = convertDateToIndex(dataDate, cpmTask.cal_id, calendarsMap);
  const workPackageMap = useWorkPackageMapSelector();

  const formatChecklistDate = (timestamp) => {
    // Convert nanoseconds and seconds to milliseconds
    const milliseconds =
      timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);

    // Create a Date object using the milliseconds
    const date = new Date(milliseconds);

    // Format the date according to the specified format: 12.05 01/11/2023
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const formattedDate = `${hours}.${minutes} ${day}/${month}/${year}`;

    return formattedDate;
  };

  const generateDeadline = (checklistItem) => {
    if (!cpmTask) return;
    const workingHours = calendarsMap.get(`whd:${cpmTask.cal_id}`)
    const halfHours = checklistItem.tMinusDays > 0 ?
        (cpmTask.lf + (checklistItem.tMinusDays * workingHours * 2)) - dataDateIndex :
        (cpmTask.ls + (checklistItem.tMinusDays * workingHours * 2)) - dataDateIndex;
    const days = Math.floor(halfHours / (workingHours * 2));

    return (
        <div className="checklist-item-deadline">
          {days <= 0 ?
              <div className="checklist-critical">
                <p className="checklist-critical-tag">{`${Math.floor(days)} day${Math.abs(days) > 1 ? 's' : ''}`}</p>
              </div> :
              days < 5 ?
                  <div className="checklist-critical">
                    <p className="checklist-near-critical-tag">{`${Math.floor(days)} day${Math.abs(days) > 1 ? 's' : ''}`}</p>
                  </div> :
                  <p style={{fontSize: "12px", paddingRight: "5px"}}>{`${Math.floor(days)} day${Math.abs(days) > 1 ? 's' : ''}`}</p>}
      </div>
    )
  }

  return (
    <>
      <div className="checklist">
        <div style={{ display: "flex", flexDirection: "column" }}>
          {taskChecklist &&
            taskChecklist.length > 0 &&
            taskChecklist.map((item: ChecklistItemModel) => {
              const taskCompletionRecord = completeChecklist.get(`${
                type === 'task' ? activeTask!.task_id : activeTask!.workPackage!.workPackageId
              }:${item.checklistItemId}`);
              const indexDeduct = item.tMinusDays * workingHours * 2;
              const dueIndex = item.tMinusDays <= 0 ?
                  cpmTask.fs ? cpmTask.fs + indexDeduct :
                      cpmTask.es + indexDeduct :
                  cpmTask.ff ? cpmTask.ff + indexDeduct :
                      cpmTask.ef + indexDeduct
              const dueDate = new Date(convertIndexToSeconds(dueIndex, cpmTask.cal_id, calendarsMap) * 1000)
              const targetDelta = Math.floor((dueIndex - dataDateIndex) / (workingHours * 2))
              return (
              <div className="checklist-item-wrapper" key={item.checklistItemId}>
                <div className="checklist-item-input-wrapper">
                  <Checkbox
                    onChange={(event, data) => {
                      if (activeTask) {
                        if (data.checked) {
                          if (type === "task") {
                            ChecklistUtils.completeCheckListItem(item.checklistItemId, activeTask, activeProject!.contractId || "", user!)
                                .catch(e => console.log(e));
                          } else {
                            ChecklistUtils.completeWorkPackageChecklistItem(workPackageMap.get(activeTask!.workPackage!.workPackageId!)!, item.checklistItemId, activeProject!.contractId || "", user!)
                                .catch(e => console.log(e));
                          }
                        } else {
                          if (type === "task") {
                            ChecklistUtils.uncheckCheckListItem(item.checklistItemId, activeTask, activeProject!.contractId || "", user!)
                                .catch(e => console.log(e));
                          } else {
                            ChecklistUtils.uncheckWorkPackageChecklistItem(workPackageMap.get(activeTask!.workPackage!.workPackageId!)!, item.checklistItemId, activeProject!.contractId || "", user!)
                                .catch(e => console.log(e));
                          }
                        }
                    }}}
                    className={classNames("checklist-item", {
                      "crossed-out": !(!taskCompletionRecord)
                    })}
                    label={item.itemName}
                    checked={!(!taskCompletionRecord)}
                    disabled={
                      (activeTask?.status === TaskStatusModel.COMPLETE ||
                      activeTask?.status ===
                        TaskStatusModel.DECLARED_COMPLETE ||
                      activeTask?.status === TaskStatusModel.IN_PROGRESS) && item.tMinusDays <= 0
                    }
                  />
                  {taskCompletionRecord && <ChecklistAttachmentInput
                      checklistItem={{...item, taskId: activeTask!.task_id}}
                      completionRecord={taskCompletionRecord}
                      deployment={'task'}
                  />}
                </div>
                <div className="checklist-dates">
                  <div className="checklist-target-date">{moment(dueDate).format("DD-MMM-YY")}</div>
                  <div className={targetDelta < 0 ? "checklist-target-date-negative" : "checklist-target-date"}>
                  {`${targetDelta} day${Math.abs(targetDelta) !== 1 ? 's' : ''}`}</div>
                  <div className="checklist-deadline-container">{generateDeadline(item)}</div>
                </div>
              </div>)
            })}
        </div>
      </div>
      {opened && <TaskEventModal
          eventText={eventText}
          opened={opened}
          setOpened={setOpened}
          task={activeTask!}
          suspendReason={"No reason"}
          // evaluationResult={"No result"}
          defaultDate=""
          activeChecklistItem={activeItem}
          setActiveItem={setActiveItem}
      />}
    </>
  );
};

export default TaskChecklist;
